import React, { useState, useEffect, useRef } from 'react';
import { Container, Card, Table, Form, Button, Badge, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { showToast as toast } from '../toastManager';

const STEP_ICONS  = { pending: '○', running: '⏳', completed: '✓', failed: '✗' };
const STEP_COLORS = { pending: '#888', running: '#0d6efd', completed: '#198754', failed: '#dc3545' };

// Polling tuning for the elements-fetch job. The endpoint returns 202+jobId
// for cold scrapes (which can take >100 s and would otherwise 504 at the
// Cloudflare edge). Cap the loop so the UI never spins forever.
const ELEMENTS_POLL_INTERVAL_MS = 1500;
const ELEMENTS_MAX_POLLS        = 200;   // ~5 minutes at 1.5 s
const ELEMENTS_MAX_FAILURES     = 3;     // consecutive transient errors tolerated

const AICampaign = ({ shop_domain, app_url }) => {
  const [landingPages, setLandingPages] = useState([]);
  const [selectedLp,   setSelectedLp]   = useState('');

  const [pageData, setPageData] = useState(null); // { text_elements, images, from_cache, fetched_at }
  const [elementsLoading, setElementsLoading] = useState(false);

  const [selectedText, setSelectedText] = useState([]);
  const [selectedImgs, setSelectedImgs] = useState([]);

  const [running,  setRunning]  = useState(false);
  const [jobSteps, setJobSteps] = useState([]);
  const [copiedStepId, setCopiedStepId] = useState(null);
  const pollRef = useRef(null);

  // Refs for the elements-fetch poll (separate lifecycle from the campaign-run poll).
  const elementsPollRef       = useRef(null);
  const elementsPollCountRef  = useRef(0);
  const elementsFailuresRef   = useRef(0);

  useEffect(() => {
    if (!shop_domain || !app_url) return;
    loadLandingPages();
    return () => {
      if (pollRef.current)         clearInterval(pollRef.current);
      if (elementsPollRef.current) clearInterval(elementsPollRef.current);
    };
  }, [shop_domain, app_url]);

  const loadLandingPages = async () => {
    try {
      const res = await axios.get(`${app_url}/api/settings/domains-and-urls/selected-pages`, { withCredentials: true });
      if (res.data.success) setLandingPages(res.data.landing_pages || []);
    } catch { /* non-fatal */ }
  };

  const stopElementsPolling = () => {
    if (elementsPollRef.current) {
      clearInterval(elementsPollRef.current);
      elementsPollRef.current = null;
    }
  };

  const startElementsPolling = (jobId) => {
    stopElementsPolling();
    elementsPollCountRef.current = 0;
    elementsFailuresRef.current  = 0;
    elementsPollRef.current = setInterval(async () => {
      elementsPollCountRef.current += 1;
      if (elementsPollCountRef.current > ELEMENTS_MAX_POLLS) {
        stopElementsPolling();
        setElementsLoading(false);
        toast.error('Still loading elements — try again shortly');
        return;
      }
      try {
        const poll = await axios.get(`${app_url}/api/firebase/job_status/${jobId}`, { withCredentials: true });
        const job  = poll.data?.job;
        // Job vanished from Redis (TTL or upstream crash) — surface as terminal.
        if (!job) {
          stopElementsPolling();
          setElementsLoading(false);
          toast.error('Element fetch lost — please retry');
          return;
        }
        elementsFailuresRef.current = 0;
        if (job.status === 'succeeded') {
          stopElementsPolling();
          setElementsLoading(false);
          const result = job.result || {};
          setPageData({
            text_elements: result.text_elements || [],
            images:        result.images        || [],
            from_cache:    result.from_cache    || false,
            fetched_at:    result.fetched_at    || null,
          });
        } else if (job.status === 'failed') {
          stopElementsPolling();
          setElementsLoading(false);
          toast.error(job.error?.message || 'Failed to fetch page elements');
        }
      } catch {
        elementsFailuresRef.current += 1;
        if (elementsFailuresRef.current >= ELEMENTS_MAX_FAILURES) {
          stopElementsPolling();
          setElementsLoading(false);
          toast.error('Lost connection while loading elements');
        }
      }
    }, ELEMENTS_POLL_INTERVAL_MS);
  };

  const fetchElements = async (lp, force = false) => {
    if (!lp) return;
    stopElementsPolling();
    setElementsLoading(true);
    setPageData(null);
    setSelectedText([]);
    setSelectedImgs([]);
    try {
      const res = await axios.post(
        `${app_url}/api/dev/re-run-modules/fetch-page-elements`,
        { url: lp, shop: shop_domain, force_refresh: force },
        { withCredentials: true }
      );

      // 202 = cold scrape was queued; poll until the worker finishes.
      if (res.status === 202 && res.data?.jobId) {
        startElementsPolling(res.data.jobId);
        return;
      }

      // 200 = cache hit, data is in the body.
      if (res.data?.success) {
        setPageData({
          text_elements: res.data.text_elements || [],
          images:        res.data.images        || [],
          from_cache:    res.data.from_cache    || false,
          fetched_at:    res.data.fetched_at    || null,
        });
        setElementsLoading(false);
      } else {
        setElementsLoading(false);
        toast.error(res.data?.message || 'Failed to load page elements');
      }
    } catch (err) {
      setElementsLoading(false);
      toast.error(err?.response?.data?.message || 'Error fetching page elements');
    }
  };

  // When user picks a different LP, auto-load its elements
  useEffect(() => {
    if (selectedLp) fetchElements(selectedLp, false);
    else { setPageData(null); setSelectedText([]); setSelectedImgs([]); }
  }, [selectedLp]);

  const toggleText = (item) => {
    setSelectedText(prev => prev.some(el => el.query_selector === item.query_selector)
      ? prev.filter(el => el.query_selector !== item.query_selector)
      : [...prev, item]);
  };

  const toggleImg = (item) => {
    setSelectedImgs(prev => prev.some(el => el.query_selector === item.query_selector)
      ? prev.filter(el => el.query_selector !== item.query_selector)
      : [...prev, item]);
  };

  const toggleAllText = (checked) => {
    setSelectedText(checked ? [...(pageData?.text_elements || [])] : []);
  };
  const toggleAllImgs = (checked) => {
    setSelectedImgs(checked ? [...(pageData?.images || [])] : []);
  };

  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const poll = await axios.get(`${app_url}/api/firebase/job_status/${jobId}`, { withCredentials: true });
        const job  = poll.data?.job;
        if (!job) return;
        if (job.steps) setJobSteps(job.steps);
        if (job.status === 'succeeded') {
          clearInterval(pollRef.current);
          setRunning(false);
          toast.success('AI Campaign completed');
        } else if (job.status === 'failed') {
          clearInterval(pollRef.current);
          setRunning(false);
          toast.error('AI Campaign failed. See status below.');
        }
      } catch {
        clearInterval(pollRef.current);
        setRunning(false);
        toast.error('Lost connection while polling status');
      }
    }, 1500);
  };

  const canRun = !!selectedLp && !running && !elementsLoading
    && (selectedText.length > 0 || selectedImgs.length > 0);

  const handleGenerate = async () => {
    if (!canRun) return;
    setRunning(true);
    setJobSteps([{
      id: `${selectedLp}__ai_campaign`,
      page: selectedLp,
      module: 'ai_campaign',
      label: `AI Campaign — ${selectedLp}`,
      status: 'pending',
    }]);

    const selections = [{
      page: selectedLp,
      is_landing_page: true,
      modules: ['ai_campaign'],
      youtube_period_months: 6,
      ai_campaign_elements: {
        text_elements: selectedText,
        images:        selectedImgs,
      },
    }];

    try {
      const res = await axios.post(
        `${app_url}/api/dev/re-run-modules`,
        { shop: shop_domain, selections },
        { withCredentials: true }
      );
      if (res.status !== 202 || !res.data?.jobId) {
        toast.error(res.data?.message || 'Failed to start AI Campaign');
        setRunning(false);
        return;
      }
      startPolling(res.data.jobId);
    } catch (err) {
      setRunning(false);
      toast.error(err?.response?.data?.message || 'Error starting AI Campaign');
    }
  };

  return (
    <Container fluid className="p-0">
      <div className="main-title mt-4">
        <h2>AI Campaign</h2>
      </div>

      <div className="main-accordion-body mt-4 p-2">

        {/* ── Card: Landing Page Selection ── */}
        <Card className="mb-3 asset-form-card">
          <Card.Header className="py-3" style={{ background: 'white', borderBottom: '1px solid #EAECF0' }}>
            <div className="sxi-accordion-header-with-icon">
              <div className="sxi-accordion-header-icon" aria-hidden="true">
                <svg width="58" height="58" viewBox="0 0 58 58" fill="none">
                  <rect x="3" y="3" width="48" height="48" rx="6" fill="#E5EEFF" stroke="#CEDEFF"/>
                  <circle cx="27" cy="27" r="10" stroke="#2056C1" strokeWidth="1.5" fill="none"/>
                  <circle cx="27" cy="27" r="4" stroke="#2056C1" strokeWidth="1.5" fill="none"/>
                </svg>
              </div>
              <span>Landing Page</span>
            </div>
          </Card.Header>
          <Card.Body>
            {landingPages.length === 0 ? (
              <p className="text-muted small mb-0">No landing pages saved yet. Add pages in Domains &amp; URLs first.</p>
            ) : (
              <Form.Select
                value={selectedLp}
                onChange={(e) => setSelectedLp(e.target.value)}
                disabled={running}
              >
                <option value="">— choose a landing page —</option>
                {landingPages.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </Form.Select>
            )}
          </Card.Body>
        </Card>

        {/* ── Card: Elements ── */}
        {selectedLp && (
          <Card className="mb-3 asset-form-card">
            <Card.Header className="py-3" style={{ background: 'white', borderBottom: '1px solid #EAECF0' }}>
              <div className="d-flex align-items-center justify-content-between">
                <div className="sxi-accordion-header-with-icon">
                  <div className="sxi-accordion-header-icon" aria-hidden="true">
                    <svg width="58" height="58" viewBox="0 0 58 58" fill="none">
                      <rect x="3" y="3" width="48" height="48" rx="6" fill="#E5EEFF" stroke="#CEDEFF"/>
                      <rect x="18" y="15" width="22" height="28" rx="2" stroke="#2056C1" strokeWidth="1.5" fill="none"/>
                      <line x1="22" y1="22" x2="36" y2="22" stroke="#2056C1" strokeWidth="1.5" strokeLinecap="round"/>
                      <line x1="22" y1="28" x2="36" y2="28" stroke="#2056C1" strokeWidth="1.5" strokeLinecap="round"/>
                      <line x1="22" y1="34" x2="32" y2="34" stroke="#2056C1" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <span>Select Elements to Customize</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  {pageData?.from_cache && (
                    <Badge bg="secondary" style={{ fontSize: 10, fontWeight: 400 }}>
                      cached{pageData.fetched_at ? ` · ${new Date(pageData.fetched_at).toLocaleDateString()}` : ''}
                    </Badge>
                  )}
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={elementsLoading || running}
                    onClick={() => fetchElements(selectedLp, true)}
                    title="Re-fetch elements from page (ignores cache)"
                  >
                    {elementsLoading ? <Spinner size="sm" animation="border" /> : 'Re-fetch'}
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {elementsLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                  <p className="mt-2 text-muted small">Fetching page elements...</p>
                </div>
              ) : !pageData ? (
                <p className="text-muted small mb-0">No elements loaded.</p>
              ) : pageData.text_elements.length === 0 && pageData.images.length === 0 ? (
                <p className="text-muted small mb-0">No elements found on this page.</p>
              ) : (
                <>
                  {pageData.text_elements.length > 0 && (
                    <div className="mb-3">
                      <div className="text-muted small mb-1 fw-semibold">Text Elements</div>
                      <Table bordered size="sm" style={{ fontSize: 12 }}>
                        <thead>
                          <tr style={{ background: '#F9FAFB' }}>
                            <th style={{ width: 36, textAlign: 'center' }}>
                              <Form.Check
                                type="checkbox"
                                checked={selectedText.length === pageData.text_elements.length}
                                onChange={(e) => toggleAllText(e.target.checked)}
                              />
                            </th>
                            <th>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pageData.text_elements.map((el, idx) => {
                            const checked = selectedText.some(s => s.query_selector === el.query_selector);
                            const display = (el.value || '').length > 80 ? el.value.slice(0, 80) + '…' : (el.value || '—');
                            return (
                              <tr key={idx} style={{ cursor: 'pointer' }} onClick={() => toggleText(el)}>
                                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                  <Form.Check
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleText(el)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </td>
                                <td style={{ verticalAlign: 'middle', wordBreak: 'break-word' }}>{display}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  )}

                  {pageData.images.length > 0 && (
                    <div>
                      <div className="text-muted small mb-1 fw-semibold">Images</div>
                      <Table bordered size="sm" style={{ fontSize: 12 }}>
                        <thead>
                          <tr style={{ background: '#F9FAFB' }}>
                            <th style={{ width: 36, textAlign: 'center' }}>
                              <Form.Check
                                type="checkbox"
                                checked={selectedImgs.length === pageData.images.length}
                                onChange={(e) => toggleAllImgs(e.target.checked)}
                              />
                            </th>
                            <th>Preview</th>
                            <th style={{ width: 80 }}>Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pageData.images.map((el, idx) => {
                            const checked = selectedImgs.some(s => s.query_selector === el.query_selector);
                            return (
                              <tr key={idx} style={{ cursor: 'pointer' }} onClick={() => toggleImg(el)}>
                                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                  <Form.Check
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleImg(el)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </td>
                                <td style={{ verticalAlign: 'middle' }}>
                                  {el.url
                                    ? <img src={el.url} alt="" height={40} style={{ objectFit: 'contain', maxWidth: 120 }} />
                                    : <span className="text-muted">—</span>}
                                </td>
                                <td style={{ verticalAlign: 'middle' }}>
                                  <Badge bg={el.type === 'bg_img' ? 'secondary' : 'primary'} style={{ fontSize: 10 }}>
                                    {el.type}
                                  </Badge>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        )}

        {/* ── Card: Run & Status ── */}
        <Card className="mb-4 asset-form-card">
          <Card.Header className="py-3" style={{ background: 'white', borderBottom: '1px solid #EAECF0' }}>
            <div className="sxi-accordion-header-with-icon">
              <div className="sxi-accordion-header-icon" aria-hidden="true">
                <svg width="58" height="58" viewBox="0 0 58 58" fill="none">
                  <rect x="3" y="3" width="48" height="48" rx="6" fill="#E5EEFF" stroke="#CEDEFF"/>
                  <polygon points="21,17 39,27 21,37" fill="#2056C1"/>
                </svg>
              </div>
              <span>Run &amp; Status</span>
            </div>
          </Card.Header>
          <Card.Body>
            <Button
              className="sxi_primary_blue_btn btn-height"
              onClick={handleGenerate}
              disabled={!canRun}
            >
              {running
                ? <><Spinner size="sm" animation="border" className="me-2" />Running...</>
                : 'Generate AI Campaign'}
            </Button>

            {jobSteps.length > 0 && (
              <div className="mt-4">
                {jobSteps.map(step => (
                  <div key={step.id} className="d-flex align-items-start gap-2 mb-1">
                    <span style={{ color: STEP_COLORS[step.status] || '#888', fontWeight: 600, minWidth: '1.2rem', lineHeight: '1.5' }}>
                      {STEP_ICONS[step.status] || '○'}
                    </span>
                    <div>
                      <span style={{ color: STEP_COLORS[step.status] || '#888', fontWeight: step.status === 'running' ? 600 : 400, fontSize: 13 }}>
                        {step.label}
                      </span>
                      {step.status === 'running' && (
                        <Spinner size="sm" animation="border" className="ms-2" style={{ width: '0.75rem', height: '0.75rem' }} />
                      )}
                      {step.error && <div className="text-danger small mt-1">{step.error}</div>}
                      {step.status === 'completed' && step.result?.lpPreview && (() => {
                        const url     = step.result.lpPreview;
                        const baseUrl = (() => { try { const u = new URL(url); return u.origin + u.pathname; } catch { return url; } })();
                        return (
                          <div className="mt-1 d-flex align-items-center"
                            style={{ border: '1px solid #dee2e6', borderRadius: 8, overflow: 'hidden', maxWidth: 400 }}>
                            <div style={{ padding: '0 10px', color: '#6c757d', flexShrink: 0 }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="2" y1="12" x2="22" y2="12"/>
                                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                              </svg>
                            </div>
                            <a href={encodeURI(url)} target="_blank" rel="noopener noreferrer" title={url}
                              style={{ flex: 1, padding: '6px 4px', fontSize: 12, color: '#495057',
                                textDecoration: 'none', overflow: 'hidden', whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis', display: 'block' }}>
                              {baseUrl}...
                            </a>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(url);
                                setCopiedStepId(step.id);
                                setTimeout(() => setCopiedStepId(null), 2000);
                              }}
                              style={{ padding: '6px 14px', color: '#000', border: 'none',
                                background: 'transparent', fontWeight: 500, fontSize: 12,
                                cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
                              {copiedStepId === step.id ? 'Copied!' : 'Copy URL'}
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>

      </div>
    </Container>
  );
};

export default AICampaign;
